/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InsuranceSanctions.module.scss';

type Translate = (key: string) => string;

const SCREEN_LISTS = [
  'OFSI consolidated list',
  'OFAC SDN list',
  'EU consolidated list',
  'UN Security Council list',
  'PEP · directors and UBOs',
];

const getOwnershipRows = (t: Translate) => [
  { who: t('Designated person A'), via: 'via MERIDIAN HOLDINGS S.à r.l. · Luxembourg RCS', pct: '25%' },
  { who: t('Designated person B'), via: 'via ORVEDA CAPITAL LTD · Cyprus registry', pct: '25%' },
  { who: t('Remaining holders'), via: t('no designation, no adverse filing'), pct: '50%', muted: true },
];

const AGG_TARGET = 50;

type UboNode = {
  x: number;
  y: number;
  w: number;
  h: number;
  kind: 'dp' | 'plain' | 'inter' | 'target';
  title?: string;
  name: string;
  source: string;
  flag?: string;
  extra?: string;
};

const getUboNodes = (t: Translate): UboNode[] => [
  {
    x: 50,
    y: 8,
    w: 280,
    h: 88,
    kind: 'dp',
    title: t('DESIGNATED PERSON'),
    name: t('Designated person A'),
    source: t('Consolidated lists · UK, EU, US'),
  },
  {
    x: 420,
    y: 8,
    w: 280,
    h: 88,
    kind: 'plain',
    title: t('NO DESIGNATION'),
    name: t('Non-designated shareholders'),
    source: t('No adverse filing on any register'),
  },
  {
    x: 790,
    y: 8,
    w: 280,
    h: 88,
    kind: 'dp',
    title: t('DESIGNATED PERSON'),
    name: t('Designated person B'),
    source: t('Consolidated lists · UK, EU, US'),
  },
  {
    x: 50,
    y: 170,
    w: 280,
    h: 88,
    kind: 'inter',
    flag: '🇱🇺',
    name: 'MERIDIAN HOLDINGS S.à r.l.',
    source: 'Luxembourg RCS · RBE beneficial owners',
    extra: t('Holdings attributed in full at 50%'),
  },
  {
    x: 790,
    y: 170,
    w: 280,
    h: 88,
    kind: 'inter',
    flag: '🇨🇾',
    name: 'ORVEDA CAPITAL LTD',
    source: 'Cyprus registry · shareholder filing',
    extra: t('Holdings attributed in full at 50%'),
  },
  {
    x: 390,
    y: 340,
    w: 340,
    h: 100,
    kind: 'target',
    flag: '🇳🇱',
    name: 'VOSTRA MARINE HOLDINGS B.V.',
    source: 'KVK Handelsregister · UBO register',
    extra: t('50% aggregate'),
  },
];

const UBO_EDGES = [
  { len: 74, d: 'M190 96 V170', hot: true, pct: '50%', px: 204, py: 138 },
  { len: 74, d: 'M930 96 V170', hot: true, pct: '50%', px: 944, py: 138 },
  { len: 244, d: 'M560 96 V340', hot: false, pct: '50%', px: 574, py: 223 },
  { len: 362, d: 'M190 258 V300 H470 V340', hot: true, pct: '25%', px: 330, py: 293 },
  { len: 362, d: 'M930 258 V300 H650 V340', hot: true, pct: '25%', px: 790, py: 293 },
];

const UBO_STEPS: { nodes?: number[]; edges?: number[] }[] = [
  { nodes: [0, 1, 2] },
  { edges: [0, 1] },
  { nodes: [3, 4] },
  { edges: [2, 3, 4] },
  { nodes: [5] },
];

const UBO_STEP_MS = 380;
const UBO_STEP_START_MS = 160;

export const InsuranceSanctions = () => {
  const { t } = useInt();
  const duoRef = useRef<HTMLDivElement>(null);
  const uboRef = useRef<HTMLDivElement>(null);
  const duoRevealed = useIntersection(duoRef, { threshold: 0.2 });
  const uboRevealed = useIntersection(uboRef, { threshold: 0.12 });
  const [agg, setAgg] = useState(0);
  const [uboNodesOn, setUboNodesOn] = useState(() => getUboNodes((k) => k).map(() => false));
  const [uboEdgesOn, setUboEdgesOn] = useState(() => UBO_EDGES.map(() => false));

  const ownershipRows = getOwnershipRows(t);
  const uboNodes = getUboNodes(t);

  useEffect(() => {
    if (!duoRevealed) {
      return undefined;
    }

    const steps = 22;
    let step = 0;
    const id = setInterval(() => {
      step += 1;
      setAgg(Math.round(AGG_TARGET * Math.min(step / steps, 1)));
      if (step >= steps) {
        clearInterval(id);
      }
    }, 26);

    return () => clearInterval(id);
  }, [duoRevealed]);

  useEffect(() => {
    if (!uboRevealed) {
      return undefined;
    }

    const timers = UBO_STEPS.map((step, i) =>
      setTimeout(
        () => {
          if (step.nodes) {
            setUboNodesOn((prev) => {
              const next = [...prev];
              step.nodes?.forEach((n) => {
                next[n] = true;
              });
              return next;
            });
          }

          if (step.edges) {
            setUboEdgesOn((prev) => {
              const next = [...prev];
              step.edges?.forEach((e) => {
                next[e] = true;
              });
              return next;
            });
          }
        },
        UBO_STEP_START_MS + i * UBO_STEP_MS,
      ),
    );

    return () => timers.forEach(clearTimeout);
  }, [uboRevealed]);

  return (
    <Section className={styles.root}>
      <div className={styles.blobA} aria-hidden="true" />
      <div className={styles.blobB} aria-hidden="true" />

      <div className={styles.body}>
        <div className={styles.head}>
          <TitleBlock
            title={t('A list screen says the name is clear. Ownership says whether you can write it.')}
            description={t(
              "Under OFAC's 50 Percent Rule an entity is blocked when designated persons hold 50% or more of it in aggregate, directly or indirectly, whether or not the entity itself appears on any list. The UK ownership test turns on the same thing. Neither is visible to a name screen.",
            )}
          />
        </div>

        <div className={cn(styles.duo, duoRevealed && styles.duoIn)} ref={duoRef}>
        <div className={styles.card}>
          <span className={cn(styles.tag, styles.tagMut)}>{t('Step one · name screening')}</span>
          <h3 className={styles.cardTitle}>{t('The named insured returns clear.')}</h3>
          <p className={styles.cardSmall}>VOSTRA MARINE HOLDINGS B.V. · {t('sample marine cargo submission.')}</p>

          <div className={styles.slList}>
            {SCREEN_LISTS.map((name) => (
              <div className={styles.sl} key={name}>
                <span className={styles.nmv}>{name}</span>
                <span className={styles.resT}>
                  <span className={styles.tick}>–</span> {t('No match')}
                </span>
              </div>
            ))}
          </div>

          <div className={styles.code}>
            <div className={styles.codeBar}>
              <span className={styles.codeDot} />
              <span className={styles.codeDot} />
              <span className={styles.codeDot} />
            </div>
            <div className={styles.codeBody}>
              screen(entity_name)
              <br />
              <span className={styles.codeDim}>→ 0 matches across 5 lists · risk binds</span>
            </div>
          </div>
        </div>

        <div className={cn(styles.card, styles.cardAccent)}>
          <span className={styles.tag}>{t('Step two · ownership resolved from registries')}</span>
          <h3 className={styles.cardTitle}>{t('Held 50% in aggregate. Blocked.')}</h3>
          <p className={styles.cardSmall}>{t('The same entity, read through its share registers.')}</p>

          <div className={styles.ownList}>
            {ownershipRows.map((row) => (
              <div className={styles.ownRow} key={row.who}>
                <span>
                  <span className={styles.who}>{row.who}</span>
                  <span className={styles.via}>{row.via}</span>
                </span>
                <span className={cn(styles.pc, row.muted && styles.pcMuted)}>{row.pct}</span>
              </div>
            ))}
          </div>

          <div className={styles.ownSum}>
            <span className={styles.sumLabelRow}>
              <svg className={styles.warnIcon} viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M10 3.2 18 16.4H2L10 3.2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                <path d="M10 8.2v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <circle cx="10" cy="13.6" r="0.95" fill="currentColor" />
              </svg>
              <span className={styles.sumLabel}>{t('Aggregate held by designated persons')}</span>
            </span>
            <span className={styles.sumPc}>{agg}%</span>
          </div>
        </div>
      </div>

      <p className={styles.duoFoot}>
        {t(
          'Neither holder crosses a threshold alone. Together they meet the 50 Percent Rule, so the entity is blocked without ever being listed. OFAC aggregates across different sanctions programmes, and indirect holdings count through any entity that is itself 50% or more owned.',
        )}
      </p>

      <div className={styles.uboHead}>
        <TitleBlock title={t('Read through every layer, in every jurisdiction it files in.')} />
      </div>

      <div className={styles.uboCard} ref={uboRef}>
        <svg
          className={styles.ugraph}
          viewBox="0 0 1120 496"
          role="img"
          aria-label={t(
            'Sample ultimate beneficial ownership graph. Designated person A holds 50 percent of Meridian Holdings in Luxembourg, which holds 25 percent of Vostra Marine Holdings in the Netherlands. Designated person B holds 50 percent of Orveda Capital in Cyprus, which holds a further 25 percent. Non-designated shareholders hold the remaining 50 percent directly. Aggregate designated ownership is 50 percent, so the entity is blocked.',
          )}
        >
          {UBO_EDGES.map((edge, i) => (
            <path
              key={edge.d}
              className={cn(styles.uedge, edge.hot && styles.hot, uboEdgesOn[i] && styles.on)}
              style={{ '--len': edge.len } as CSSProperties}
              d={edge.d}
            />
          ))}
          {UBO_EDGES.map((edge) => {
            const badgeWidth = edge.pct === '50%' ? 46 : 48;

            return (
              <g key={`${edge.px}-${edge.py}`}>
                <rect
                  className={styles.pctBg}
                  x={edge.px - badgeWidth / 2}
                  y={edge.py - 15}
                  width={badgeWidth}
                  height="20"
                  rx="5"
                />
                <text className={cn(styles.upct, edge.hot && styles.hot)} x={edge.px} y={edge.py}>
                  {edge.pct}
                </text>
              </g>
            );
          })}

          {uboNodes.map((node, i) => (
            <g
              key={node.name}
              className={cn(styles.un, uboNodesOn[i] && styles.on)}
              transform={`translate(${node.x},${node.y})`}
            >
              <title>{node.name}</title>
              <rect
                className={cn(styles.ubox, node.kind === 'dp' && styles.uboxDp, node.kind === 'target' && styles.uboxT)}
                width={node.w}
                height={node.h}
                rx={node.kind === 'target' ? 12 : 10}
              />

              {node.kind === 'dp' && (
                <>
                  <text className={cn(styles.ulab, styles.ulabDp)} x="18" y="28">
                    {node.title}
                  </text>
                  <text className={styles.uname} x="18" y="54">
                    {node.name}
                  </text>
                  <text className={styles.usrc} x="18" y="76">
                    {node.source}
                  </text>
                </>
              )}

              {node.kind === 'plain' && (
                <>
                  <text className={styles.ulab} x="18" y="28">
                    {node.title}
                  </text>
                  <text className={styles.uname} x="18" y="54">
                    {node.name}
                  </text>
                  <text className={styles.usrc} x="18" y="76">
                    {node.source}
                  </text>
                </>
              )}

              {node.kind === 'inter' && (
                <>
                  <text className={styles.uflag} x="18" y="31">
                    {node.flag}
                  </text>
                  <text className={styles.uname} x="46" y="31">
                    {node.name}
                  </text>
                  <text className={styles.usrc} x="18" y="55">
                    {node.source}
                  </text>
                  <text className={styles.ueff} x="18" y="77">
                    {node.extra}
                  </text>
                </>
              )}

              {node.kind === 'target' && (
                <>
                  <text className={styles.uflag} x="20" y="33">
                    {node.flag}
                  </text>
                  <text className={cn(styles.ulab, styles.ulabTg)} x="48" y="33">
                    {t('THE INSURED')}
                  </text>
                  <rect className={styles.blockedBadge} x="238" y="19" width="84" height="20" rx="10" />
                  <text className={styles.blockedText} x="280" y="33">
                    {t('BLOCKED')}
                  </text>
                  <text className={styles.unameLg} x="20" y="65">
                    {node.name}
                  </text>
                  <text className={styles.usrc} x="20" y="87">
                    {node.source}
                  </text>
                  <text className={styles.ueffEnd} x="320" y="87">
                    {node.extra}
                  </text>
                </>
              )}
            </g>
          ))}

          <text className={styles.ucap} x="560" y="482" textAnchor="middle">
            {t(
              'Neither holder crosses a threshold alone. Attributed in full through each intermediary, they aggregate to 50%.',
            )}
          </text>
        </svg>
      </div>
      <p className={styles.uboNote}>
        {t(
          "Where a designated person holds 50% or more of an intermediary, OFAC attributes that intermediary's holdings in full rather than multiplying them through. Two separate 25% attributions therefore aggregate to 50%, and the insured is blocked without appearing on any list.",
        )}
      </p>
      </div>
    </Section>
  );
};
